/**
 * Duration display configuration for the Timers (Life Events) page.
 *
 * Edits the duration_* half of the life_events_display namespace:
 * largest calendar unit, precision (how many units of the years → months →
 * weeks → days chain are rendered), the h:m:s clock toggle, and the
 * workdays-remaining caption. Rendering already honours these in TimerCard;
 * this dialog only mutates the settings. Changes apply live via onChange —
 * the page persists them through the namespace write path.
 */
import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
} from '@mui/material';

import {
  DurationLargestUnit,
  LifeEventsDisplaySettings,
} from '../contexts/PreferencesContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface DurationDisplayDialogProps {
  open: boolean;
  onClose: () => void;
  settings: LifeEventsDisplaySettings;
  onChange: (patch: Partial<LifeEventsDisplaySettings>) => void;
}

const unitOptions: Array<{ value: DurationLargestUnit; label: string }> = [
  { value: 'years', label: 'Years' },
  { value: 'months', label: 'Months' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'days', label: 'Days' },
];

const DurationDisplayDialog: React.FC<DurationDisplayDialogProps> = ({
  open,
  onClose,
  settings,
  onChange,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Duration display</DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="duration-largest-unit-label">Largest unit</InputLabel>
            <Select
              labelId="duration-largest-unit-label"
              label="Largest unit"
              value={settings.duration_largest_unit}
              onChange={(e) =>
                onChange({
                  duration_largest_unit: e.target.value as DurationLargestUnit,
                })
              }
            >
              {unitOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="duration-precision-label">Detail level</InputLabel>
            <Select
              labelId="duration-precision-label"
              label="Detail level"
              value={settings.duration_precision}
              onChange={(e) =>
                onChange({ duration_precision: Number(e.target.value) })
              }
            >
              {[1, 2, 3, 4].map((precision) => (
                <MenuItem key={precision} value={precision}>
                  {precision} unit{precision === 1 ? '' : 's'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={settings.duration_show_clock}
                onChange={(e) =>
                  onChange({ duration_show_clock: e.target.checked })
                }
              />
            }
            label="Show hours : minutes : seconds"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.duration_show_workdays}
                onChange={(e) =>
                  onChange({ duration_show_workdays: e.target.checked })
                }
              />
            }
            label="Show workdays remaining (countdowns)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DurationDisplayDialog;
